import {useContext, useEffect, useState} from 'react';
import {useOutletContext, useParams} from 'react-router-dom';

import {Typography} from '@mui/material';
import {CartesianGrid, XAxis, YAxis, Tooltip, Bar, BarChart, Cell, PieChart, Pie} from 'recharts';

import {request, URLs} from '../../api';
import {GetGroupContestStatisticsResponse} from '../../api/responses/ContestViewResponseTypes';
import AppContext from '../../context/AppContext';
import {ContestContextType} from '../../pages/contest/Contest';

export const Statistics = () => {
  const {dispatchError} = useContext(AppContext);

  const {contest} = useOutletContext<ContestContextType>();

  const {groupContest} = useParams();
  const [gradesDistrubution, setGradesDistrubution] = useState<GetGroupContestStatisticsResponse['gradesDistrubution']>(
    []
  );
  const [averageGrades, setAverageGrades] = useState<GetGroupContestStatisticsResponse['averageGrades']>([]);

  useEffect(() => {
    request<never, never, GetGroupContestStatisticsResponse>(URLs.getGroupContestStatistics(Number(groupContest)), {
      method: 'GET',
      successCallback(response) {
        const {gradesDistrubution, averageGrades} = response;
        setGradesDistrubution(gradesDistrubution);
        setAverageGrades(averageGrades);
      },
      errorCallback() {
        dispatchError({
          errorMessage: 'There was an error. Please try again later.',
        });
      },
    });
  }, []);

  return (
    <div className="grid grid-cols-12 justify-center items-center rounded-2xl bg-card-main">
      <div className="col-span-6">
        <div className="flex flex-col items-center">
          <PieChart width={600} height={300}>
            <Pie
              dataKey="value"
              isAnimationActive={false}
              data={gradesDistrubution.map((item) => ({
                value: item.frequency,
                name: item.grade,
              }))}
              outerRadius={120}
              label={({cx, cy, midAngle, innerRadius, outerRadius, name}) => {
                const radius = 25 + innerRadius + (outerRadius - innerRadius);
                const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
                const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

                return (
                  <text x={x} y={y} fill="#F2F2F2" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                    {name}
                  </text>
                );
              }}
            >
              {gradesDistrubution.map((entry, index) => (
                <Cell key={`pie-cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-card-light px-3 py-3 rounded-xl">
                      <p>Grade: {payload[0].name}</p>
                      <p>Count: {payload[0].value}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
          <Typography align="center" sx={{width: 250}}>
            Grades frequency distribution chart
          </Typography>
        </div>
      </div>
      <div className="col-span-6">
        <div className="flex flex-col items-center">
          <BarChart
            width={600}
            height={300}
            data={averageGrades.map((item) => ({
              value: item.averageGrade,
              name: contest.problems.find((problem) => problem.id === item.problemId)?.title ?? 'Unknown',
            }))}
          >
            <CartesianGrid stroke="#ffffff" strokeDasharray="5 5" />
            <XAxis dataKey="name" stroke="#ffffff" />
            <YAxis stroke="#ffffff" domain={['auto', 'auto']} />
            <Bar dataKey="value" barSize={30} fill={'#AB8ECC'}>
              {averageGrades.map((entry, index) => (
                <Cell key={`bar-cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Bar>
            <Tooltip
              cursor={{fill: 'rgba(0, 0, 0, 0.2)'}}
              content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-card-light px-3 py-3 rounded-xl">
                      <p>
                        <b>{`${payload[0].payload.name}`}</b>
                      </p>
                      <p>{`Avg: ${parseFloat(String(payload[0].value ?? '-1')).toFixed(2)}`}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </BarChart>
          <Typography align="center" sx={{width: 250}}>
            Average grade per problem
          </Typography>
        </div>
      </div>
    </div>
  );
};

const colors = [
  '#0088FE',
  '#0070D1',
  '#0058A4',
  '#004078',
  '#00C49F',
  '#00A887',
  '#008C6F',
  '#007057',
  '#FFBB28',
  '#D89F20',
  '#B1841A',
  '#8A6813',
  '#FF8042',
  '#D76B38',
  '#B0572E',
  '#894224',
  '#FF02A1',
  '#D6018A',
  '#AE0173',
  '#86005C',
  '#A5921F',
  '#897B1A',
  '#6E6515',
  '#524F10',
  '#FFBB28',
  '#D89F20',
  '#B1841A',
  '#8A6813',
  '#FF02A1',
  '#D6018A',
  '#AE0173',
  '#86005C',
  '#AB8FF1',
  '#9578CC',
  '#7F62A7',
  '#694B82',
  '#257831',
  '#1E6228',
  '#174C1F',
  '#103616',
];
